import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Loader2,
  XCircle,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useVerifyCitationMutation } from '@/services/api/demandReviewService';
import {
  CitationVerificationStatus,
  DemandReviewCitation,
  getVerificationStatusLabel,
} from '@/services/types/demand-review-types';

interface CitationListProps {
  citations: DemandReviewCitation[];
  isLoading: boolean;
  selectedCitationId: string | null;
  onSelectCitation: (id: string | null) => void;
  matterId: string;
  workspaceId: string;
}

function getStatusIcon(status: CitationVerificationStatus) {
  switch (status) {
    case CitationVerificationStatus.VERIFIED_SUPPORTED:
      return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    case CitationVerificationStatus.VERIFIED_NOT_SUPPORTED:
      return <XCircle className="h-4 w-4 text-red-600" />;
    case CitationVerificationStatus.VERIFIED_PARTIAL:
      return <AlertCircle className="h-4 w-4 text-yellow-600" />;
    case CitationVerificationStatus.IN_PROGRESS:
      return <Loader2 className="h-4 w-4 animate-spin text-blue-600" />;
    case CitationVerificationStatus.PENDING:
    default:
      return <Clock className="h-4 w-4 text-gray-400" />;
  }
}

function getStatusBadgeVariant(
  status: CitationVerificationStatus,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case CitationVerificationStatus.VERIFIED_SUPPORTED:
      return 'default';
    case CitationVerificationStatus.VERIFIED_NOT_SUPPORTED:
      return 'destructive';
    case CitationVerificationStatus.VERIFIED_PARTIAL:
      return 'secondary';
    default:
      return 'outline';
  }
}

export const CitationList = ({
  citations,
  isLoading,
  selectedCitationId,
  onSelectCitation,
  matterId,
  workspaceId,
}: CitationListProps) => {
  const [verifyCitation, { isLoading: isVerifying }] =
    useVerifyCitationMutation();

  const handleVerify = async (citationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await verifyCitation({ matterId, workspaceId, citationId });
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (citations.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          No citations found. Upload exhibits and match citations first.
        </CardContent>
      </Card>
    );
  }

  return (
    <ScrollArea className="h-full max-h-[calc(100vh-350px)]">
      <div className="space-y-2 pr-4">
        {citations.map((citation) => (
          <Card
            key={citation.id}
            className={`cursor-pointer transition-colors hover:bg-muted/50 ${
              selectedCitationId === citation.id
                ? 'border-primary bg-muted/50'
                : ''
            }`}
            onClick={() => onSelectCitation(citation.id)}
          >
            <CardHeader className="p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  {getStatusIcon(citation.verification_status)}
                  <span className="font-mono text-sm font-semibold">
                    {citation.bates_number}
                  </span>
                </div>
                <Badge
                  variant={getStatusBadgeVariant(citation.verification_status)}
                >
                  {getVerificationStatusLabel(citation.verification_status)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="px-3 pb-3 pt-0">
              <p className="line-clamp-2 text-xs text-muted-foreground">
                {citation.context_text}
              </p>
              <div className="mt-2 flex items-center justify-between">
                <div className="text-xs text-muted-foreground">
                  {citation.exhibit_id ? (
                    <span>Page {citation.exhibit_page_number ?? '?'}</span>
                  ) : (
                    <span className="text-yellow-600">No exhibit matched</span>
                  )}
                </div>
                {citation.exhibit_id &&
                  citation.verification_status ===
                    CitationVerificationStatus.PENDING && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => handleVerify(citation.id, e)}
                      disabled={isVerifying}
                    >
                      {isVerifying ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        'Verify'
                      )}
                    </Button>
                  )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </ScrollArea>
  );
};
